import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyLogo from "./LogoB.png";
import { useContactDialog } from "../common";
import {
  colors,
  zIndex,
  borderRadius,
  typography,
  shadows,
} from "../../theme/tokens";

// ヘッダーナビ(フローティングのピル型)
// 各項目はルートページ内セクションへのアンカーリンク(スムーススクロール)

const pages = [
  { title: "SERVICE", id: "service", ariaLabel: "事業内容セクションへ移動" },
  { title: "CASE", id: "case", ariaLabel: "実績紹介セクションへ移動" },
  { title: "LAB", id: "lab", ariaLabel: "開発室セクションへ移動" },
  {
    title: "ABOUT US",
    id: "about",
    ariaLabel: "会社についてセクションへ移動",
  },
];

const ResponsiveAppBar = () => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const { open: openContactDialog } = useContactDialog();

  const normalizedPath = pathname.replace(/\/+$/, "") || "/";

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleNavigate = (id: string) => {
    // ルート上ならその場でスムーススクロール、別ページからは /#id へ遷移
    if (normalizedPath === "/") {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    navigate(`/#${id}`);
    handleCloseNavMenu();
  };

  const handleKeyDown = (event: React.KeyboardEvent, id: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigate(id);
    }
  };

  const handleContact = () => {
    openContactDialog();
    handleCloseNavMenu();
  };

  const isActive = (id: string) => normalizedPath === "/" && hash === `#${id}`;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        borderBottom: "none",
        top: { xs: 0, md: "20px" },
        left: 0,
        right: 0,
        px: { xs: 0, md: "10vw" },
        zIndex: zIndex.header,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { md: "1200px" },
          mx: { xs: 0, md: "auto" },
          backgroundColor: {
            xs: colors.neutral[0],
            md: "rgba(255, 255, 255, 0.9)",
          },
          backdropFilter: { md: "blur(12px)" },
          WebkitBackdropFilter: { md: "blur(12px)" },
          borderRadius: { xs: 0, md: borderRadius.full },
          border: { xs: "none", md: `1px solid ${colors.neutral[200]}` },
          borderBottom: {
            xs: `1px solid ${colors.neutral[200]}`,
            md: "none",
          },
          boxShadow: { xs: "none", md: shadows.base },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: "60px !important",
            px: { xs: "16px", md: "28px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* ロゴ */}
          <Box
            component="a"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              minHeight: "44px",
              order: { xs: 1, md: 0 },
              "&:focus-visible": {
                outline: `2px solid ${colors.primary[500]}`,
                outlineOffset: "2px",
                borderRadius: "4px",
              },
            }}
            aria-label="Lamactホームページへ移動"
          >
            <img
              src={CompanyLogo}
              alt="Lamactロゴ"
              height="36"
              width="135"
              style={{ display: "block" }}
            />
          </Box>

          {/* モバイル版メニューボタン */}
          <Box sx={{ display: { xs: "flex", md: "none" }, order: 2 }}>
            <IconButton
              size="large"
              aria-label="ナビゲーションメニューを開く"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: colors.neutral[800],
                minHeight: "44px",
                minWidth: "44px",
                "&:focus-visible": {
                  outline: `2px solid ${colors.primary[500]}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              TransitionComponent={Fade}
              sx={{
                display: { xs: "block", md: "none" },
                "& .MuiPaper-root": {
                  borderRadius: "12px",
                  boxShadow: shadows.md,
                  border: `1px solid ${colors.neutral[200]}`,
                  mt: 1,
                  minWidth: "200px",
                  zIndex: zIndex.dropdown,
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.title}
                  onClick={() => handleNavigate(page.id)}
                  onKeyDown={(e) => handleKeyDown(e, page.id)}
                  aria-label={page.ariaLabel}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: colors.primary[50],
                    },
                    "&:focus": {
                      backgroundColor: colors.primary[50],
                      outline: "none",
                    },
                  }}
                >
                  <Typography
                    textAlign="center"
                    sx={{
                      fontWeight: 500,
                      color: colors.neutral[900],
                    }}
                  >
                    {page.title}
                  </Typography>
                </MenuItem>
              ))}
              <MenuItem
                onClick={handleContact}
                aria-label="お問い合わせを開く"
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": { backgroundColor: colors.primary[50] },
                  "&:focus": {
                    backgroundColor: colors.primary[50],
                    outline: "none",
                  },
                }}
              >
                <Typography
                  textAlign="center"
                  sx={{ fontWeight: 600, color: colors.primary[600] }}
                >
                  お問い合わせ
                </Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* デスクトップ版ナビゲーション */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {pages.map((page) => {
              const active = isActive(page.id);
              return (
                <Button
                  key={page.title}
                  onClick={() => handleNavigate(page.id)}
                  onKeyDown={(e) => handleKeyDown(e, page.id)}
                  aria-label={page.ariaLabel}
                  disableElevation
                  sx={{
                    color: active ? colors.primary[700] : colors.neutral[700],
                    fontWeight: 500,
                    px: "20px",
                    py: "8px",
                    borderRadius: borderRadius.full,
                    textTransform: "none",
                    fontSize: "15px",
                    backgroundColor: active
                      ? colors.primary[50]
                      : "transparent",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: active
                        ? colors.primary[50]
                        : colors.neutral[100],
                      transform: "none",
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${colors.primary[500]}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}
            <Button
              onClick={handleContact}
              aria-label="お問い合わせを開く"
              variant="contained"
              disableElevation
              sx={{
                ml: 1,
                color: colors.neutral[0],
                backgroundColor: colors.primary[600],
                fontWeight: typography.fontWeight.medium,
                px: "22px",
                py: "9px",
                borderRadius: borderRadius.full,
                textTransform: "none",
                fontSize: "15px",
                boxShadow: shadows.sm,
                "&:hover": {
                  backgroundColor: colors.primary[700],
                  boxShadow: shadows.md,
                  transform: "translateY(-1px)",
                },
              }}
            >
              お問い合わせ
            </Button>
          </Box>
        </Toolbar>
      </Box>
    </AppBar>
  );
};
export default ResponsiveAppBar;
